import React from "react";
import { Typography, List, ListItem, ListItemText, Link } from "@mui/material";
import { NavLink } from "react-router-dom";

export type DocsSection = { label: string; content: React.ReactNode };

export const sections: DocsSection[] = [
  {
    label: "Etusivu",
    content: (
      <>
        <Typography paragraph>
          Hoitokunta vastaa tiekunnan käytännön toiminnasta ja teiden
          kunnossapidosta. Tiehoitokunta.fi helpottaa hallinnointia
          keskittämällä päätökset, asiakirjat ja viestinnän yhteen paikkaan.
        </Typography>
        <List dense>
          <ListItem>
            <ListItemText primary="Luo kokous ja esityslista: Kokoukset → Uusi kokous → lisää pykälät (päätökset)." />
          </ListItem>
          <ListItem>
            <ListItemText primary="Kirjaa päätökset pöytäkirjaan ja liitä asiakirjat (tarjoukset, kartat)." />
          </ListItem>
          <ListItem>
            <ListItemText primary="Päivitä yksiköinti muutosten jälkeen (esim. uudet kiinteistöt, käyttötarkoitukset)." />
          </ListItem>
          <ListItem>
            <ListItemText primary="Lähetä viestit osakkaille (ilmoitukset, muistutukset, kokouskutsut)." />
          </ListItem>
          <ListItem>
            <ListItemText primary="Seuraa taloutta: Talous → Tulos & Tase (nykyinen + edellinen tilikausi vertailuna)." />
          </ListItem>
        </List>
      </>
    ),
  },
  {
    label: "Osakkaat",
    content: (
      <>
        <Typography paragraph>
          Osakasrekisteri kokoaa omistajatiedot, yhteystiedot ja maksutilanteen.
          Pidä tiedot ajan tasalla laskutuksen ja viestinnän sujuvuuden
          varmistamiseksi.
        </Typography>
        <List dense>
          <ListItem>
            <ListItemText primary="Lisää tai muokkaa osakas: Osakkaat → Uusi osakas / Muokkaa." />
          </ListItem>
          <ListItem>
            <ListItemText primary="Liitä osakkaalle kiinteistöt ja yksiköinnit (näkyy laskutuksessa)." />
          </ListItem>
          <ListItem>
            <ListItemText primary="Käytä suodattimia ryhmäviestintään (esim. tien tai maksutilanteen mukaan)." />
          </ListItem>
          <ListItem>
            <ListItemText primary="Tarkista yhteystiedot ennen laskujen lähetystä (posti/sähköposti)." />
          </ListItem>
        </List>
      </>
    ),
  },
  {
    label: "Tiet",
    content: (
      <>
        <Typography paragraph>
          Tiet-moduulissa hallinnoit tiekunnan tieosuuksia ja
          kunnossapitoluokitusta. Tämä tieto toimii pohjana kustannusten jaolle
          ja kunnossapitosuunnittelulle.
        </Typography>
        <List dense>
          <ListItem>
            <ListItemText primary="Lisää tie: Tiet → Uusi tie (nimi, pituus, kunnossapitotaso)." />
          </ListItem>
          <ListItem>
            <ListItemText primary="Jaa alateihin tarvittaessa ja liitä niihin kiinteistöt." />
          </ListItem>
          <ListItem>
            <ListItemText primary="Luo kunnossapidon kustannusarviot tiekohtaisesti ja linkitä yksiköintiin." />
          </ListItem>
        </List>
      </>
    ),
  },
  {
    label: "Kiinteistöt",
    content: (
      <>
        <Typography paragraph>
          Kiinteistörekisterissä hallitaan osoitteet, pinta-alat, omistussuhteet
          ja tieyhteydet. Kiinteistöjen linkitys tieosiin on tärkeää yksiköintiä
          ja laskutusta varten.
        </Typography>
        <List dense>
          <ListItem>
            <ListItemText primary="Lisää kiinteistö ja omistajat: Kiinteistöt → Uusi kiinteistö." />
          </ListItem>
          <ListItem>
            <ListItemText primary="Liitä kiinteistö tieosaan; tarkenna käyttö (vakituinen, vapaa-ajan, maatila, tms.)." />
          </ListItem>
          <ListItem>
            <ListItemText primary="Päivitä yksiköintiarvot muutosten yhteydessä (käyttö, etäisyys, tieosuus)." />
          </ListItem>
        </List>
      </>
    ),
  },
  {
    label: "Talous",
    content: (
      <>
        <Typography variant="h6" gutterBottom>
          Kirjanpito ja raportointi
        </Typography>
        <Typography paragraph>
          Talous-moduuli sisältää tositteet, viennit, tilikaudet sekä raportit.
          Päivämäärät esitetään eurooppalaisessa muodossa (pp.kk.vvvv).
          Tuloslaskelmassa ja taseessa näkyvät sekä kuluvan että edellisen
          tilikauden luvut vertailua varten.
        </Typography>
        <Typography variant="subtitle1" gutterBottom>
          1) Tilikausi
        </Typography>
        <List dense>
          <ListItem>
            <ListItemText primary="Aktiivinen tilikausi on aina yksi kerrallaan (Asetukset → Tilikaudet)." />
          </ListItem>
          <ListItem>
            <ListItemText primary="Uusi tilikausi: määritä aloitus- ja päättymispäivä. Edellinen jää historiaan." />
          </ListItem>
        </List>
        <Typography variant="subtitle1" gutterBottom>
          2) Tositteet
        </Typography>
        <List dense>
          <ListItem>
            <ListItemText primary="Luo tosite: Talous → Tositteet → Uusi tosite (päivämäärä, selite, liite)." />
          </ListItem>
          <ListItem>
            <ListItemText primary="Lisää vientirivit: vähintään debet + kredit, summien täsmättävä." />
          </ListItem>
          <ListItem>
            <ListItemText primary="Valitse tilit tiekunnan tilikartasta (esim. Pankkitili, Tiekulut)." />
          </ListItem>
          <ListItem>
            <ListItemText primary="Muokkaa/korjaa tosite ennen lukitusta; tarv. poista ja syötä uudestaan." />
          </ListItem>
        </List>
        <Typography variant="subtitle1" gutterBottom>
          3) Raportit
        </Typography>
        <List dense>
          <ListItem>
            <ListItemText primary="Tuloslaskelma: tuotot ja kulut tilikausittain + vertailuvuosi rinnalla." />
          </ListItem>
          <ListItem>
            <ListItemText primary="Tase: varat ja velat tilikauden lopussa + vertailuvuosi." />
          </ListItem>
          <ListItem>
            <ListItemText primary="Tulosta tai tallenna PDF:ksi raporttisivulta." />
          </ListItem>
        </List>
        <Typography variant="subtitle1" gutterBottom>
          4) Vinkit
        </Typography>
        <List dense>
          <ListItem>
            <ListItemText primary="Käytä liitteitä (PDF/kuva) tositteilla – helpottaa tarkistuksia ja arkistointia." />
          </ListItem>
          <ListItem>
            <ListItemText primary="Jos tositteet koskevat edellistä tilikautta, varmista että se on avattuna vertailua varten." />
          </ListItem>
        </List>
      </>
    ),
  },
  {
    label: "Yksiköinti",
    content: (
      <>
        <Typography paragraph>
          Yksiköinti määrittää kustannusten jaon eri osakkaiden kesken tien
          käytön perusteella.
        </Typography>
        <List dense>
          <ListItem>
            <ListItemText primary="Avaa nykyinen yksiköinti tai luo uusi versio muutosten jälkeen." />
          </ListItem>
          <ListItem>
            <ListItemText primary="Päivitä perusteet: käyttö, etäisyys, tien tyyppi, raskas liikenne." />
          </ListItem>
          <ListItem>
            <ListItemText primary="Esikatsele jakauma ja vaikutus osakkaiden maksuosuuksiin." />
          </ListItem>
          <ListItem>
            <ListItemText primary="Vahvista ja julkaise – tieto siirtyy laskutukseen ja raportteihin." />
          </ListItem>
        </List>
      </>
    ),
  },
  {
    label: "Viestit",
    content: (
      <>
        <Typography paragraph>
          Viestityökalulla viestit osakkaille ja hoitokunnalle nopeasti. Käytä
          valmiita vastaanottajaryhmiä ja liitä tarvittaessa dokumentteja.
        </Typography>
        <List dense>
          <ListItem>
            <ListItemText primary="Uusi viesti: Viestit → Luo viesti (otsikko, sisältö)." />
          </ListItem>
          <ListItem>
            <ListItemText primary="Valitse kohderyhmä (kaikki osakkaat, tien osakkaat, hoitokunta)." />
          </ListItem>
          <ListItem>
            <ListItemText primary="Liitteet: lisää kokouskutsu, pöytäkirja tai lasku PDF:nä." />
          </ListItem>
          <ListItem>
            <ListItemText primary="Seuraa lähetyksen tilaa ja avausprosentteja (jos käytössä)." />
          </ListItem>
        </List>
      </>
    ),
  },
  {
    label: "Kokous",
    content: (
      <>
        <Typography paragraph>
          Kokousmoduulilla kutsut kokoukset, laadit esityslistat ja kirjaat
          pöytäkirjat päätöksineen. Kaikki säilyy yhdenmukaisena arkistossa.
        </Typography>
        <List dense>
          <ListItem>
            <ListItemText primary="Luo kokous (päivä, paikka, etälinkki tarv.)." />
          </ListItem>
          <ListItem>
            <ListItemText primary="Lisää esityslistan pykälät ja liitteet." />
          </ListItem>
          <ListItem>
            <ListItemText primary="Kokouksen jälkeen kirjaa päätökset pöytäkirjaan ja julkaise osakkaille." />
          </ListItem>
        </List>
      </>
    ),
  },
  {
    label: "Tiedostot",
    content: (
      <>
        <Typography paragraph>
          Tiedostot-sivulla tallennat tiekuntasi tärkeät dokumentit talteen.
        </Typography>

        <Typography variant="subtitle1" gutterBottom>
          Näin lisäät tiedoston
        </Typography>
        <List dense>
          <ListItem>
            <ListItemText primary="Napsauta sivun oikeasta yläkulmasta ”Lisää”." />
          </ListItem>
          <ListItem>
            <ListItemText primary="Valitse tiedosto avautuvasta valitsimesta ja vahvista tallennus." />
          </ListItem>
        </List>

        <Typography paragraph>
          Tallennetut tiedostot ovat aina saatavilla. Voit esikatsella niitä
          suoraan sivulla tai ladata ne omalle tietokoneellesi.
        </Typography>
      </>
    ),
  },
  {
    label: "Kartta",
    content: (
      <>
        <Typography paragraph>
          Karttasivu auttaa tarkastelemaan tiekuntaa ja tekemään toimenpiteitä
          suoraan kartalla.
        </Typography>
        <List dense>
          <ListItem>
            <ListItemText primary="Siirrä karttaa vetämällä, zoomaa hiiren rullalla tai oikean yläkulman +/–-painikkeilla." />
          </ListItem>
          <ListItem>
            <ListItemText primary="Läheltä näkyvät kiinteistörajat ja kiinteistötunnukset." />
          </ListItem>
          <ListItem>
            <ListItemText primary="Klikkaa kiinteistöä nähdäksesi tiedot tai lisätäksesi, muokataksesi ja poistaaksesi sen tiekunnasta." />
          </ListItem>
          <ListItem>
            <ListItemText primary="Tiet näkyvät sinisinä viivoina, tiekunnan alkupiste punaisena pisteenä ja tiekunnan kiinteistöt tummanharmaana." />
          </ListItem>
          <ListItem>
            <ListItemText primary="Vasemman yläkulman karttatyökalu avaa valikon: pituusmittaus, pinta-alamittaus, pisteen lisäys ja tyhjennys." />
          </ListItem>
          <ListItem>
            <ListItemText primary="Valitse taustakartta: Mapbox (oletus), MML Maastokartta, Taustakartta tai Ortokuva." />
          </ListItem>
        </List>
      </>
    ),
  },
  {
    label: "Yhteys",
    content: (
      <>
        <Typography paragraph>
          Yhteyssivulla voit ottaa yhteyttä ylläpitoon. Yhteydenoton
          lähettämisen jälkeen Keskustelut-sivulle avautuu uusi keskustelu
          ylläpidon kanssa, johon saat vastaukset. Saat sähköposti-ilmoituksen
          aina, kun ylläpito vastaa.
        </Typography>
        <Link component={NavLink} to="/contact">
          Yhteys
        </Link>
      </>
    ),
  },
];
